// BillingService: Frontend service layer for billing operations
// Provides clean interface to Edge Functions with error handling and type safety

import { supabase } from '@/config/supabase'
import type {
  SubscriptionPlan,
  SubscriptionStatusResponse,
  CreditBalance,
  CreditDeductionRequest,
  CreditDeductionResponse,
  CreateCheckoutRequest,
  CheckoutSessionResponse,
  CustomerPortalResponse,
  CreditHistoryResponse
} from '@/types/billing'

class BillingService {
  private baseUrl: string

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
    console.log('🔧 BillingService initialized with baseUrl:', this.baseUrl)
  }

  /**
   * Get JWT token for authenticated requests
   */
  private async getAuthToken(): Promise<string> {
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error || !session?.access_token) {
      throw new Error('User not authenticated')
    }
    
    return session.access_token
  }

  /**
   * Make authenticated request to Edge Function
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken()
    const fullUrl = `${this.baseUrl}${endpoint}`

    console.log(`🌐 Making request to: ${fullUrl}`)
    console.log(`🔑 Using method: ${options.method || 'GET'}`)

    const response = await fetch(fullUrl, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    })

    console.log(`📡 Response status: ${response.status} ${response.statusText}`)
    console.log(`📡 Response headers:`, Object.fromEntries(response.headers.entries()))

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.log(`❌ Error response data:`, errorData)
      throw new BillingServiceError(
        errorData.message || 'Request failed',
        response.status,
        errorData.error,
        errorData.details
      )
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T
    }

    return response.json()
  }

  /**
   * Get all available subscription plans
   */
  async getSubscriptionPlans(activeOnly: boolean = true): Promise<SubscriptionPlan[]> {
    // TEMPORARY FIX: Force client-side fallback since Edge Functions return placeholder data
    console.log('🔄 Forcing client-side subscription plans lookup (Edge Functions disabled)')

    // Return the same fallback plans as useSubscription hook (without Enterprise)
    const fallbackPlans: SubscriptionPlan[] = [
      {
        id: 'starter',
        name: 'Starter',
        description: 'For hobby photographers',
        credits_per_month: 500,
        price_monthly_cents: 600,  // $6/month to match landing page
        price_annual_cents: 5800,  // $58/year (~17% discount)
        is_active: true,
        is_popular: false,
        sort_order: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 'professional',
        name: 'Professional',
        description: 'For serious photographers',
        credits_per_month: 2000,
        price_monthly_cents: 1600,  // $16/month to match landing page
        price_annual_cents: 15400,  // $154/year (~20% discount)
        is_active: true,
        is_popular: true,
        sort_order: 2,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 'business',
        name: 'Business',
        description: 'For agencies & teams',
        credits_per_month: 10000,
        price_monthly_cents: 5000,  // $50/month to match landing page
        price_annual_cents: 48000,  // $480/year (~20% discount)
        is_active: true,
        is_popular: false,
        sort_order: 3,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      // NOTE: Removed Enterprise plan to match landing page - no Enterprise option
    ]

    return activeOnly ? fallbackPlans.filter(plan => plan.is_active) : fallbackPlans

    // TODO: Re-enable Edge Function calls once they're fixed to return real user data
    // const queryParam = activeOnly ? '?active_only=true' : ''
    // return this.makeRequest<SubscriptionPlan[]>(`/subscription-plans${queryParam}`)
  }

  /**
   * Get current user's subscription status
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    // Try Edge Function first (works in both development and production when deployed)
    try {
      console.log('🔍 Attempting Edge Function subscription status call...')
      const result = await this.makeRequest<SubscriptionStatusResponse>('/subscription-status')
      console.log('✅ Edge Function subscription status successful')
      return result
    } catch (error) {
      console.warn('🔄 Edge Function failed, falling back to client-side lookup:', error)

      // Log the specific error for debugging
      if (error instanceof BillingServiceError) {
        console.warn(`Edge Function error: ${error.statusCode} - ${error.message}`)
      }

      return this.getSubscriptionStatusClientSide()
    }
  }

  /**
   * Get subscription status from database directly (fallback)
   * Enhanced with automatic profile creation and robust error handling
   */
  private async getSubscriptionStatusClientSide(): Promise<SubscriptionStatusResponse> {
    console.log('🔧 Client-side subscription status lookup started')

    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new BillingServiceError('User not authenticated', 401, 'unauthorized')
    }

    console.log('👤 User authenticated:', user.email)

    // Ensure user profile exists (with automatic creation for auth-only accounts)
    await this.ensureUserProfileExists(user)

    // Get user profile with subscription information
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('subscription_status, current_plan_id, credits_balance, credits_used_this_month, trial_credits_used')
      .eq('id', user.id)
      .single()

    if (profileError || !profile) {
      console.error('❌ Profile not found after creation attempt')
      console.error('Profile error:', profileError)

      // REMOVED: Dangerous fallback that masks profile creation issues
      // Throw error so the UI can show appropriate message
      throw new BillingServiceError(
        'User profile not found. Please contact support if this persists.',
        404,
        'profile_not_found',
        { profileError }
      )
    }

    console.log('📊 Profile data:', profile)

    // Determine plan name and credits based on current_plan_id
    let planName = 'Free Trial'
    let creditsPerMonth = 100

    if (profile.current_plan_id && profile.subscription_status === 'active') {
      // Get plan details from subscription_plans table
      const { data: plan } = await supabase
        .from('subscription_plans')
        .select('name, credits_per_month')
        .eq('id', profile.current_plan_id)
        .single()

      if (plan) {
        planName = plan.name
        creditsPerMonth = plan.credits_per_month
      } else {
        // Fallback based on plan ID
        switch (profile.current_plan_id) {
          case 'starter':
            planName = 'Starter'
            creditsPerMonth = 500
            break
          case 'professional':
            planName = 'Professional'
            creditsPerMonth = 2000
            break
          case 'business':
            planName = 'Business'
            creditsPerMonth = 10000
            break
          default:
            planName = profile.current_plan_id
            creditsPerMonth = 100
        }
      }
    }

    // Ensure proper status handling
    let status = profile.subscription_status || 'trial'

    // Validate status - fallback to trial if invalid
    if (!['trial', 'active', 'cancelled', 'past_due'].includes(status)) {
      console.warn(`Invalid subscription status '${status}', defaulting to trial`)
      status = 'trial'
    }

    const response = {
      status,
      plan_name: planName,
      credits_balance: profile.credits_balance || (status === 'trial' ? 100 : 0),
      credits_per_month: creditsPerMonth
    }

    console.log('📋 Client-side subscription status result:', response)
    console.log('📊 Profile data used:', {
      subscription_status: profile.subscription_status,
      current_plan_id: profile.current_plan_id,
      credits_balance: profile.credits_balance,
      planName,
      creditsPerMonth,
      finalStatus: status
    })
    return response
  }

  /**
   * Get current user's credit balance
   */
  async getCreditBalance(): Promise<CreditBalance> {
    try {
      return await this.makeRequest<CreditBalance>('/credit-balance')
    } catch (error) {
      console.warn('Edge Function failed, attempting client-side credit balance lookup:', error)
      return this.getCreditBalanceClientSide()
    }
  }

  /**
   * Client-side credit balance fallback
   */
  private async getCreditBalanceClientSide(): Promise<CreditBalance> {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new BillingServiceError('User not authenticated', 401, 'unauthorized')
    }

    // Ensure user profile exists
    await this.ensureUserProfileExists(user)

    // Get user profile with credit information
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('credits_balance, credits_used_this_month, subscription_status, current_plan_id, trial_credits_used')
      .eq('id', user.id)
      .single()

    if (profileError || !profile) {
      console.log('🚧 Database not available, using localStorage credit balance')
      return this.getLocalStorageCreditBalance(user)
    }

    // Return actual balance from profile
    return {
      balance: profile.credits_balance || 0,
      used_this_month: profile.credits_used_this_month || 0,
      limit_per_month: await this.getCreditsForProfile(profile),
      resets_at: this.getNextResetDate().toISOString(),
      trial_credits_remaining: profile.subscription_status === 'trial' ? Math.max(0, 100 - (profile.trial_credits_used || 0)) : undefined
    }
  }

  /**
   * Get credit balance from localStorage
   */
  private getLocalStorageCreditBalance(user: any): CreditBalance {
    const storageKey = `credits_${user.id}`
    const usageKey = `credits_used_${user.id}`

    const storedBalance = localStorage.getItem(storageKey)
    const storedUsage = localStorage.getItem(usageKey)

    const balance = storedBalance ? parseInt(storedBalance) : 100
    const used = storedUsage ? parseInt(storedUsage) : 0

    console.log(`💾 Retrieved localStorage balance: ${balance}, used: ${used}`)

    return {
      balance,
      used_this_month: used,
      limit_per_month: 100, // Free trial default
      resets_at: this.getNextResetDate().toISOString(),
      trial_credits_remaining: Math.max(0, 100 - used)
    }
  }

  /**
   * Get credit limit for subscription tier (legacy)
   */
  private getCreditsForTier(tier: string): number {
    const tierLimits = {
      'free': 100,
      'starter': 500,
      'professional': 2000,
      'business': 10000
    }
    return tierLimits[tier as keyof typeof tierLimits] || 100
  }

  /**
   * Get credit limit for user profile (works with current database schema)
   */
  private async getCreditsForProfile(profile: any): Promise<number> {
    // If user has trial status, return 100 credits
    if (profile.subscription_status === 'trial' || !profile.current_plan_id) {
      return 100
    }

    // If user has a plan ID, look up the plan details
    try {
      const { data: plan, error } = await supabase
        .from('subscription_plans')
        .select('credits_per_month')
        .eq('id', profile.current_plan_id)
        .single()

      if (error || !plan) {
        console.warn('Could not find subscription plan, defaulting to 100 credits')
        return 100
      }

      return plan.credits_per_month || 100
    } catch (error) {
      console.warn('Error fetching subscription plan:', error)
      return 100
    }
  }

  /**
   * Calculate next monthly reset date
   */
  private getNextResetDate(): Date {
    const now = new Date()
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    return nextMonth
  }

  /**
   * Deduct credits for AI processing (with idempotency)
   */
  async deductCredits(request: CreditDeductionRequest): Promise<CreditDeductionResponse> {
    try {
      // Try Edge Function first
      return await this.makeRequest<CreditDeductionResponse>('/deduct-credits', {
        method: 'POST',
        body: JSON.stringify(request),
      })
    } catch (error) {
      console.warn('Edge Function failed, attempting client-side credit deduction:', error)

      // Fallback to client-side deduction
      return this.deductCreditsClientSide(request)
    }
  }

  /**
   * Client-side credit deduction fallback
   */
  private async deductCreditsClientSide(request: CreditDeductionRequest): Promise<CreditDeductionResponse> {
    console.log('🔧 Client-side credit deduction fallback started')

    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new BillingServiceError('User not authenticated', 401, 'unauthorized')
    }

    try {
      // Try to ensure user profile exists first
      await this.ensureUserProfileExists(user)

      // Check for existing transaction with same idempotency key (if transactions table exists)
      const { data: existingTransaction } = await supabase
        .from('credit_transactions')
        .select('id, amount, user_id')
        .eq('idempotency_key', request.idempotency_key)
        .eq('user_id', user.id)
        .maybeSingle()

      if (existingTransaction) {
        console.log('🔄 Found existing transaction, returning cached result')
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('credits_balance')
          .eq('id', user.id)
          .single()

        return {
          success: true,
          balance_after: profile?.credits_balance || 2000, // fallback default
          transaction_id: existingTransaction.id
        }
      }
    } catch (transactionError) {
      console.warn('🚧 Credit transactions table not available, proceeding with simple deduction')
    }

    try {
      // Try to use the database RPC function
      const { data: result, error: deductError } = await supabase
        .rpc('deduct_credits', {
          user_uuid: user.id,
          credit_amount: request.amount,
          action_description: request.reason,
          action_metadata: request.metadata || null
        })

      if (deductError) {
        throw deductError
      }

      const deductionResult = result[0]
      if (!deductionResult?.success) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('credits_balance')
          .eq('id', user.id)
          .single()

        throw new BillingServiceError(
          'Not enough credits available for this operation',
          400,
          'insufficient_credits',
          {
            requested: request.amount,
            available: userProfile?.credits_balance || 0
          }
        )
      }

      return {
        success: true,
        balance_after: deductionResult.new_balance,
        transaction_id: deductionResult.transaction_id || 'manual-' + Date.now()
      }

    } catch (rpcError) {
      console.warn('🚧 Database RPC function not available, using direct table updates')
      return this.manualCreditDeduction(user, request)
    }
  }

  /**
   * Manual credit deduction when database functions are not available
   */
  private async manualCreditDeduction(user: any, request: CreditDeductionRequest): Promise<CreditDeductionResponse> {
    console.log('🔧 Performing manual credit deduction')

    // Get current user profile
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('credits_balance, credits_used_this_month')
      .eq('id', user.id)
      .single()

    if (profileError || !profile) {
      console.log('🚧 Database not available, using localStorage credit tracking')
      return this.handleLocalStorageCredits(user, request)
    }

    const currentBalance = profile.credits_balance || 0
    if (currentBalance < request.amount) {
      throw new BillingServiceError(
        'Not enough credits available for this operation',
        400,
        'insufficient_credits',
        {
          requested: request.amount,
          available: currentBalance
        }
      )
    }

    // Manually update the user profile
    const newBalance = currentBalance - request.amount
    const newUsage = (profile.credits_used_this_month || 0) + request.amount

    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({
        credits_balance: newBalance,
        credits_used_this_month: newUsage,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)

    if (updateError) {
      console.error('Failed to update user profile:', updateError)
      throw new BillingServiceError(
        'Failed to update credit balance',
        500,
        'database_error',
        updateError
      )
    }

    console.log(`✅ Manual credit deduction successful: ${currentBalance} → ${newBalance}`)

    return {
      success: true,
      balance_after: newBalance,
      transaction_id: 'manual-' + Date.now()
    }
  }

  /**
   * Handle credit tracking using localStorage when database is unavailable
   */
  private handleLocalStorageCredits(user: any, request: CreditDeductionRequest): CreditDeductionResponse {
    const storageKey = `credits_${user.id}`
    const usageKey = `credits_used_${user.id}`

    // Get current balance from localStorage
    const storedBalance = localStorage.getItem(storageKey)
    const storedUsage = localStorage.getItem(usageKey)

    const currentBalance = storedBalance ? parseInt(storedBalance) : 2000 // Start with 2000 credits
    const currentUsage = storedUsage ? parseInt(storedUsage) : 0

    console.log(`💾 Current localStorage balance: ${currentBalance}, usage: ${currentUsage}`)

    if (currentBalance < request.amount) {
      throw new BillingServiceError(
        'Not enough credits available for this operation',
        400,
        'insufficient_credits',
        {
          requested: request.amount,
          available: currentBalance
        }
      )
    }

    // Deduct credits and update localStorage
    const newBalance = currentBalance - request.amount
    const newUsage = currentUsage + request.amount

    localStorage.setItem(storageKey, newBalance.toString())
    localStorage.setItem(usageKey, newUsage.toString())

    console.log(`✅ localStorage credit deduction: ${currentBalance} → ${newBalance} (used: ${newUsage})`)

    // Trigger a custom event to notify components of balance change
    window.dispatchEvent(new CustomEvent('creditsUpdated', {
      detail: { balance: newBalance, used: newUsage }
    }))

    return {
      success: true,
      balance_after: newBalance,
      transaction_id: 'localStorage-' + Date.now()
    }
  }

  /**
   * Ensure user profile exists, create if missing
   * Enhanced with retry logic and better error handling for production
   */
  private async ensureUserProfileExists(user: any): Promise<void> {
    console.log('🔍 Checking user profile existence for:', user.id)

    const { data: existingProfile, error: checkError } = await supabase
      .from('user_profiles')
      .select('id, subscription_status, credits_balance')
      .eq('id', user.id)
      .maybeSingle()

    if (checkError) {
      console.warn('⚠️ Error checking user profile:', checkError)
      // Continue anyway - might be temporary database issue
    }

    if (!existingProfile) {
      console.log('🔧 Creating missing user profile for auth-only account')

      const profileData = {
        id: user.id,
        user_id: user.id,
        email: user.email || '',
        full_name: user.user_metadata?.full_name || null,
        avatar_url: user.user_metadata?.avatar_url || null,
        subscription_status: 'trial',
        current_plan_id: null, // Free trial doesn't have a plan ID
        credits_balance: 100,
        credits_used_this_month: 0,
        trial_credits_used: 0
      }

      // Retry logic for profile creation
      let retryCount = 0
      const maxRetries = 3

      while (retryCount < maxRetries) {
        const { error: createError } = await supabase
          .from('user_profiles')
          .insert(profileData)

        if (!createError) {
          console.log('✅ User profile created successfully on attempt', retryCount + 1)
          return
        }

        retryCount++
        console.warn(`❌ Failed to create user profile (attempt ${retryCount}/${maxRetries}):`, createError)

        if (retryCount < maxRetries) {
          // Wait briefly before retry
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }

      console.error('💥 Failed to create user profile after all retries')
      // Don't throw error - let the system continue with fallback handling
    } else {
      console.log('✅ User profile exists:', {
        status: existingProfile.subscription_status,
        credits: existingProfile.credits_balance
      })
    }
  }

  /**
   * Get credit transaction history
   */
  async getCreditHistory(limit: number = 20, offset: number = 0): Promise<CreditHistoryResponse> {
    return this.makeRequest<CreditHistoryResponse>(`/credit-history?limit=${limit}&offset=${offset}`)
  }

  /**
   * Create Stripe checkout session for subscription
   */
  async createCheckoutSession(request: CreateCheckoutRequest): Promise<CheckoutSessionResponse> {
    console.log('🔍 BillingService - Creating checkout session')
    console.log('🔍 BillingService - Request payload:', request)
    console.log('🔍 BillingService - Success URL in request:', request.success_url)

    // Try authenticated request first (for logged-in users upgrading)
    // This ensures user_id is added to Stripe metadata for proper account linking
    try {
      console.log('🔍 BillingService - Attempting authenticated checkout')
      const result = await this.makeRequest<CheckoutSessionResponse>('/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify(request),
      })
      console.log('✅ BillingService - Authenticated checkout successful')
      console.log('🔍 BillingService - Response from Edge Function:', result)
      return result
    } catch (authError) {
      // Fall back to guest checkout if not authenticated
      console.log('🔄 BillingService - Authenticated checkout failed, falling back to guest mode')
      console.log('🔍 BillingService - Auth error:', authError)

      const result = await this.makeGuestRequest<CheckoutSessionResponse>('/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify(request),
      })
      console.log('✅ BillingService - Guest checkout successful')
      console.log('🔍 BillingService - Response from Edge Function:', result)
      return result
    }
  }

  /**
   * Make unauthenticated request for guest checkout
   */
  private async makeGuestRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Use anon key for guest requests to bypass Supabase auth middleware
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        ...options.headers,
      },
      ...options,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new BillingServiceError(
        errorData.message || 'Request failed',
        response.status,
        errorData.error,
        errorData.details
      )
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T
    }

    return response.json()
  }

  /**
   * Create Stripe customer portal session
   */
  async createCustomerPortal(returnUrl?: string): Promise<CustomerPortalResponse> {
    const body = returnUrl ? JSON.stringify({ return_url: returnUrl }) : undefined

    try {
      console.log('🔍 Attempting Edge Function customer portal call...')
      const result = await this.makeRequest<CustomerPortalResponse>('/customer-portal', {
        method: 'POST',
        body,
      })
      console.log('✅ Edge Function customer portal successful')
      return result
    } catch (error) {
      console.error('❌ Customer portal Edge Function failed:', error)

      // Provide specific error messages based on the error type
      if (error instanceof BillingServiceError) {
        console.warn(`Customer portal error: ${error.statusCode} - ${error.message}`)

        // Handle trial user protection errors
        if (error.statusCode === 403) {
          const errorData = error.getData()

          if (errorData?.user_guidance) {
            throw new BillingServiceError(
              errorData.message || 'Access denied - trial users cannot access billing portal',
              403,
              'trial_user_blocked',
              errorData
            )
          }

          throw new BillingServiceError(
            'You do not have permission to access the billing portal. Please upgrade to a paid plan.',
            403,
            'access_denied'
          )
        }

        if (error.statusCode === 404) {
          const errorData = error.getData()

          // Check if it's a billing account setup issue
          if (errorData?.error === 'Billing account setup incomplete') {
            throw new BillingServiceError(
              errorData.message || 'Billing account setup incomplete',
              404,
              'billing_setup_incomplete',
              errorData
            )
          }

          throw new BillingServiceError(
            'Customer portal Edge Function not found. Please check if the function is properly deployed.',
            404,
            'edge_function_missing'
          )
        }

        if (error.statusCode === 401) {
          throw new BillingServiceError(
            'Authentication required for customer portal access. Please log in and try again.',
            401,
            'authentication_required'
          )
        }
      }

      // Re-throw the original error for other cases
      throw error
    }
  }

  /**
   * Track analytics event (no auth required)
   */
  async trackAnalyticsEvent(event: {
    event_type: string
    page_variant?: string
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    metadata?: Record<string, any>
  }): Promise<void> {
    await fetch(`${this.baseUrl}/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    })
    // Analytics tracking is fire-and-forget, don't throw on errors
  }

  /**
   * Helper: Check if user has sufficient credits
   */
  async hasCredits(amount: number = 1): Promise<boolean> {
    try {
      const balance = await this.getCreditBalance()
      return balance.balance >= amount
    } catch (error) {
      console.error('Error checking credits:', error)
      return false
    }
  }

  /**
   * Helper: Generate idempotency key for credit deduction
   */
  generateIdempotencyKey(context: string, timestamp?: number): string {
    const ts = timestamp || Date.now()
    const random = Math.random().toString(36).substring(2, 8)
    return `${context}_${ts}_${random}`
  }

  /**
   * Helper: Safely deduct credits for image processing
   */
  async deductCreditsForImageProcessing(
    imageId: string,
    processingType: string = 'ai_metadata_generation',
    amount: number = 1
  ): Promise<CreditDeductionResponse> {
    const idempotencyKey = this.generateIdempotencyKey(`img_${imageId}`)
    
    return this.deductCredits({
      amount,
      reason: 'image_processing',
      idempotency_key: idempotencyKey,
      metadata: {
        image_id: imageId,
        processing_type: processingType,
      }
    })
  }

  /**
   * Helper: Safely deduct credits for batch processing
   */
  async deductCreditsForBatchProcessing(
    batchId: string,
    imageCount: number,
    processingType: string = 'bulk_metadata_generation'
  ): Promise<CreditDeductionResponse> {
    const idempotencyKey = this.generateIdempotencyKey(`batch_${batchId}`)
    
    return this.deductCredits({
      amount: imageCount,
      reason: 'batch_processing',
      idempotency_key: idempotencyKey,
      metadata: {
        batch_id: batchId,
        image_count: imageCount,
        processing_type: processingType,
      }
    })
  }
}

/**
 * Custom error class for billing service errors
 */
export class BillingServiceError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errorCode?: string,
    public details?: any
  ) {
    super(message)
    this.name = 'BillingServiceError'
  }

  /**
   * Check if error is due to insufficient credits
   */
  isInsufficientCredits(): boolean {
    return this.errorCode === 'insufficient_credits'
  }

  /**
   * Check if error is due to authentication issues
   */
  isAuthError(): boolean {
    return this.statusCode === 401 || this.errorCode === 'unauthorized'
  }

  /**
   * Check if error is due to validation issues
   */
  isValidationError(): boolean {
    return this.statusCode === 400 && this.errorCode === 'validation_error'
  }
}

// Export singleton instance
export const billingService = new BillingService()
export default billingService